import { createTheme, ThemeOptions } from "@mui/material/styles";

// Using colors from _variables.sass
const primaryColorSolid = "rgb(140, 150, 169)"; // Lighter gray
const complementaryColor = "rgb(251, 171, 53)";
const complementaryTransparent = "rgba(251, 171, 53, 0.2)";
const backgroundColor = "rgba(140, 150, 169, 0.1)"; // Lighter gray background
const secComplementaryColor = "#F1ECE7";
const dangerColor = "rgb(237, 79, 50)";
const warningColor = "rgb(255, 169, 0)";

const themeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: primaryColorSolid,
      light: "rgba(140, 150, 169, 0.6)",
      dark: "rgba(90, 100, 119, 1)", // Keep darker shade for contrast
      contrastText: "#ffffff",
    },
    secondary: {
      main: complementaryColor,
      light: "rgba(251, 171, 53, 0.8)",
      dark: "rgba(201, 131, 13, 1)",
      contrastText: "#ffffff",
    },
    background: {
      default: secComplementaryColor,
      paper: "#ffffff",
    },
    text: {
      primary: primaryColorSolid,
      secondary: "rgba(90, 100, 119, 0.7)",
    },
    error: {
      main: dangerColor,
    },
    warning: {
      main: warningColor,
    },
    success: {
      main: "#4caf50",
    },
    info: {
      main: primaryColorSolid,
    },
  },
  typography: {
    fontFamily:
      '"Inter", "SF Pro Display", "Segoe UI", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.3,
      letterSpacing: "-0.015em",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.75rem",
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.5rem",
      lineHeight: 1.4,
      letterSpacing: "-0.005em",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: "1.125rem",
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
      fontWeight: 400,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
      fontWeight: 400,
    },
    button: {
      fontWeight: 600,
      fontSize: "0.875rem",
      textTransform: "none" as const,
      letterSpacing: "0.02em",
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.4,
      fontWeight: 500,
    },
    overline: {
      fontSize: "0.75rem",
      lineHeight: 1.4,
      fontWeight: 600,
      letterSpacing: "0.1em",
      textTransform: "uppercase" as const,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0px 2px 4px rgba(90, 100, 119, 0.1)",
    "0px 4px 8px rgba(90, 100, 119, 0.15)",
    "0px 6px 12px rgba(90, 100, 119, 0.2)",
    "0px 8px 16px rgba(90, 100, 119, 0.25)",
    "0px 10px 20px rgba(90, 100, 119, 0.3)",
    "0px 12px 24px rgba(90, 100, 119, 0.35)",
    "0px 14px 28px rgba(90, 100, 119, 0.4)",
    "0px 16px 32px rgba(90, 100, 119, 0.45)",
    "0px 18px 36px rgba(90, 100, 119, 0.5)",
    "0px 20px 40px rgba(90, 100, 119, 0.55)",
    "0px 22px 44px rgba(90, 100, 119, 0.6)",
    "0px 24px 48px rgba(90, 100, 119, 0.65)",
    "0px 26px 52px rgba(90, 100, 119, 0.7)",
    "0px 28px 56px rgba(90, 100, 119, 0.75)",
    "0px 30px 60px rgba(90, 100, 119, 0.8)",
    "0px 32px 64px rgba(90, 100, 119, 0.85)",
    "0px 34px 68px rgba(90, 100, 119, 0.9)",
    "0px 36px 72px rgba(90, 100, 119, 0.95)",
    "0px 38px 76px rgba(90, 100, 119, 1)",
    "0px 40px 80px rgba(90, 100, 119, 1)",
    "0px 42px 84px rgba(90, 100, 119, 1)",
    "0px 44px 88px rgba(90, 100, 119, 1)",
    "0px 46px 92px rgba(90, 100, 119, 1)",
    "0px 48px 96px rgba(90, 100, 119, 1)",
  ],
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          padding: "10px 24px",
          fontSize: "0.875rem",
          fontWeight: 600,
          textTransform: "none",
          boxShadow: "0px 2px 4px rgba(90, 100, 119, 0.2)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "0px 4px 8px rgba(90, 100, 119, 0.3)",
            transform: "translateY(-2px)",
          },
          "&:active": {
            transform: "translateY(0px)",
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${complementaryColor} 0%, rgba(251, 171, 53, 0.8) 100%)`,
          color: "#ffffff",
          "&:hover": {
            background: `linear-gradient(135deg, rgba(251, 171, 53, 0.9) 0%, ${complementaryColor} 100%)`,
          },
        },
        outlined: {
          borderColor: primaryColorSolid,
          color: primaryColorSolid,
          borderWidth: "2px",
          "&:hover": {
            borderWidth: "2px",
            backgroundColor: backgroundColor,
            borderColor: complementaryColor,
            color: complementaryColor,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          boxShadow: "0px 4px 12px rgba(90, 100, 119, 0.15)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "0px 8px 24px rgba(90, 100, 119, 0.25)",
            transform: "translateY(-4px)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: complementaryColor,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: complementaryColor,
              borderWidth: "2px",
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          backdropFilter: "blur(20px)",
          background: "rgba(255, 255, 255, 0.95)",
          boxShadow: "0px 4px 12px rgba(90, 100, 119, 0.15)",
        },
        elevation1: {
          boxShadow: "0px 2px 4px rgba(90, 100, 119, 0.1)",
        },
        elevation2: {
          boxShadow: "0px 4px 8px rgba(90, 100, 119, 0.15)",
        },
        elevation3: {
          boxShadow: "0px 6px 12px rgba(90, 100, 119, 0.2)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: "0px",
          backdropFilter: "blur(20px)",
          background: "rgba(90, 100, 119, 0.95)",
          boxShadow: "0px 2px 8px rgba(90, 100, 119, 0.2)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: "0px",
          backgroundColor: primaryColorSolid,
          backdropFilter: "blur(20px)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "scale(1.05)",
            backgroundColor: complementaryTransparent,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          margin: "4px 8px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: complementaryTransparent,
            transform: "translateX(8px)",
          },
          "&.Mui-selected": {
            backgroundColor: complementaryColor,
            color: "#ffffff",
            "&:hover": {
              backgroundColor: complementaryColor,
              opacity: 0.9,
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontWeight: 600,
          fontSize: "0.875rem",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "20px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "scale(1.05)",
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: primaryColorSolid,
          borderRadius: "8px",
          fontSize: "0.875rem",
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
